import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useLocation } from 'react-router-dom';
import { X, Calendar, Clock, Edit2 } from 'lucide-react';
import Button from '../../components/ui/button';
import Spinner from '../../components/ui/spinner';
import { formatCurrency } from '../../utils/formatters';
import { useClientBookingManageViewModel } from '../../view-model/client/client-booking-manage-view-model';
import { useTranslation } from 'react-i18next';
import OptionsSelector from '../../components/client/options-selector';

const BookingManage: React.FC = observer(() => {
  const location = useLocation();
  const viewModel = useClientBookingManageViewModel();
  const { t, i18n } = useTranslation();

  const currentLanguage = i18n.language;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get('idBooking');

    if (id) {
      viewModel.fetchBooking(id).then();
    } else {
      viewModel.setNoBookingId();
    }
  }, [location.search, viewModel]);

  useEffect(() => {
    if (viewModel.isEditMode && viewModel.cartItems.length > 0) {
      viewModel.loadAvailabilityOverview();
      // Réinitialiser la sélection de créneau car les disponibilités changent
      viewModel.selectedSlot = null;
      viewModel.selectedDate = null;
      viewModel.availableSlots = [];
    }
  }, [viewModel.isEditMode, viewModel.cartItems.length, JSON.stringify(viewModel.cartItems.map(item => ({
    serviceId: item.service.id,
    optionIds: item.options.map(o => o.id).sort()
  })))]);

  if (viewModel.getLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-white flex items-center justify-center p-4">
        <Spinner size="lg" />
      </div>
    );
  }

  if (viewModel.getError || !viewModel.getBooking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-white flex flex-col items-center justify-center p-4 text-center">
        <div className="max-w-md w-full space-y-6 bg-white p-8 rounded-xl shadow-md">
          <h1 className="text-2xl font-bold text-red-500">{t('bookingManage.errorTitle', 'Erreur')}</h1>
          <p className="text-gray-600">{viewModel.getError}</p>
          <Button
            onClick={() => window.location.href = `/${currentLanguage}`}
            className="w-full bg-green-600 hover:bg-green-700 text-white"
          >
            {t('bookingManage.backToHome', 'Retour à l\'accueil')}
          </Button>
        </div>
      </div>
    );
  }

  const booking = viewModel.getBooking;

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-primary/5 to-white relative overflow-hidden py-10 px-4">
      {/* Decorative elements */}
      <div className="absolute left-0 top-1/4 w-64 h-64 bg-primary/5 rounded-full -z-10 blur-3xl"></div>
      <div className="absolute right-0 bottom-1/4 w-64 h-64 bg-primary/5 rounded-full -z-10 blur-3xl"></div>

      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
          <header className="text-center">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              {viewModel.isCancelled
                ? t('bookingManage.cancelled', 'Réservation annulée')
                : viewModel.isEditMode
                  ? t('bookingManage.editMode', 'Modifier la réservation')
                  : t('bookingManage.details', 'Détails de la réservation')}
            </h1>
            <p className="mt-1 text-primary text-lg font-semibold">
              {booking.businessName}
            </p>
          </header>

          {/* Client info */}
          <section className="grid sm:grid-cols-2 gap-4 text-gray-800 mt-6">
            <div>
              <p className="text-sm text-gray-500">{t('bookingManage.client', 'Client')}</p>
              <p className="text-base font-medium">{booking.clientFirstName} {booking.clientLastName}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('bookingManage.date', 'Date')}</p>
              <p className="text-base font-medium">{viewModel.formatDate(booking.startTime)}</p>
            </div>
          </section>
        </div>

        {/* Cart / Services section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {t('bookingManage.services', 'Services')} ({viewModel.cartItems.length})
            </h2>
            {viewModel.canEdit && !viewModel.isEditMode && (
              <button
                onClick={() => viewModel.setIsEditMode(true)}
                className="flex items-center gap-2 text-primary hover:text-primary-dark transition-colors"
              >
                <Edit2 size={18} />
                <span className="text-sm font-medium">{t('bookingManage.edit', 'Modifier')}</span>
              </button>
            )}
          </div>

          {/* Cart items */}
          <div className="space-y-3">
            {viewModel.cartItems.map((item, index) => (
              <div
                key={index}
                className="relative bg-white rounded-lg p-4 border border-gray-200"
              >
                {/* Remove button (only in edit mode and if more than 1 service) */}
                {viewModel.isEditMode && viewModel.cartItems.length > 1 && (
                  <button
                    onClick={() => viewModel.removeServiceFromCart(index)}
                    className="absolute top-2 right-2 p-1 rounded-full hover:bg-red-100 text-red-600 transition-colors"
                  >
                    <X size={18} />
                  </button>
                )}

                <div className="flex justify-between items-start pr-8">
                  <div>
                    <h3 className="font-semibold text-gray-800">{item.service.name}</h3>
                    <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                      <Clock size={14} />
                      <span>{item.duration} min</span>
                    </div>
                  </div>
                  <span className="font-bold text-primary">{formatCurrency(item.price)}</span>
                </div>

                {/* Options */}
                {item.options.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    {item.options.map((opt, optIndex) => (
                      <div key={optIndex} className="flex justify-between text-sm text-gray-600">
                        <span>+ {opt.name}</span>
                        <span>{formatCurrency(opt.price)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Add service section (only in edit mode) */}
          {viewModel.isEditMode && !viewModel.selectedService && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                {t('bookingManage.addService', 'Ajouter un service')}
              </h3>
              <div className="grid grid-cols-1 gap-3">
                {viewModel.services.map(service => (
                  <div
                    key={service.id}
                    onClick={() => viewModel.selectServiceToAdd(service)}
                    className="bg-white rounded-lg p-3 border border-gray-200 hover:border-primary cursor-pointer transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-800 text-sm">{service.name}</h4>
                        {service.description && (
                          <p className="text-xs text-gray-600 line-clamp-2 mt-1">{service.description}</p>
                        )}
                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                          <Clock size={12} />
                          <span>{service.duration} min</span>
                        </div>
                      </div>
                      <div className="text-sm font-bold text-primary ml-3">
                        {formatCurrency(service.price)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Service configuration (when selecting a service to add) */}
          {viewModel.isEditMode && viewModel.selectedService && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">
                  {viewModel.selectedService.name}
                </h3>
                <button
                  onClick={() => viewModel.cancelServiceSelection()}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Options selector */}
              {viewModel.optionCategories.length > 0 && (
                <div className="mb-4">
                  <OptionsSelector
                    optionCategories={viewModel.optionCategories}
                    selectedOptions={viewModel.selectedOptions}
                    onSelectOption={viewModel.selectOption.bind(viewModel)}
                  />
                </div>
              )}

              {/* Add to cart button */}
              <div className="space-y-2">
                <Button
                  onClick={() => viewModel.addServiceToCart()}
                  className="w-full bg-primary text-white hover:bg-primary-dark"
                  disabled={
                    Array.isArray(viewModel.optionCategories) &&
                    viewModel.optionCategories
                      .filter(cat => cat.isRequired)
                      .some(cat => !viewModel.selectedOptions.some(opt => opt.categoryId === cat.id))
                  }
                >
                  {t('bookingManage.addToCart', 'Ajouter au panier')}
                </Button>
                <button
                  onClick={() => viewModel.cancelServiceSelection()}
                  className="w-full text-sm text-gray-600 hover:text-gray-800 py-2"
                >
                  {t('bookingManage.cancel', 'Annuler')}
                </button>
              </div>
            </div>
          )}

          {/* Total */}
          <div className="flex justify-between items-center border-t border-gray-200 mt-6 pt-4">
            <span className="text-base font-semibold text-gray-900">
              {t('bookingManage.total', 'Total')}
            </span>
            <span className="text-xl font-bold text-primary">
              {formatCurrency(viewModel.totalPrice)}
            </span>
          </div>

          {/* Price difference (in edit mode) */}
          {viewModel.isEditMode && viewModel.priceDifference !== 0 && (
            <div className={`flex justify-between items-center mt-2 p-3 rounded-lg ${
              viewModel.priceDifference > 0 ? 'bg-orange-50' : 'bg-green-50'
            }`}>
              <span className="text-sm font-medium">
                {viewModel.priceDifference > 0
                  ? t('bookingManage.toPay', 'À payer')
                  : t('bookingManage.toRefund', 'À rembourser')}
              </span>
              <span className={`font-bold ${
                viewModel.priceDifference > 0 ? 'text-orange-600' : 'text-green-600'
              }`}>
                {viewModel.priceDifference > 0 ? '+' : ''}{formatCurrency(viewModel.priceDifference)}
              </span>
            </div>
          )}
        </div>

        {/* Time slot section (in edit mode) */}
        {viewModel.isEditMode && (
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {t('bookingManage.timeSlot', 'Créneau horaire')}
            </h2>

            {/* Current time slot */}
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">
                {t('bookingManage.currentSlot', 'Créneau actuel')}
              </p>
              <div className="flex items-center gap-2 text-gray-900">
                <Calendar size={18} className="text-primary" />
                <span className="font-medium">{viewModel.formatDate(booking.startTime)}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600 mt-1">
                <Clock size={16} />
                <span className="text-sm">{viewModel.originalTotalDuration} min</span>
              </div>
            </div>

            {/* Duration change warning */}
            {viewModel.hasDurationChanged && (
              <div className={`mb-4 p-4 rounded-lg border-2 ${
                viewModel.needsTimeSlotSelection
                  ? 'bg-orange-50 border-orange-200'
                  : 'bg-blue-50 border-blue-200'
              }`}>
                <div className="flex items-start gap-3">
                  <Clock size={20} className={viewModel.needsTimeSlotSelection ? 'text-orange-600 flex-shrink-0 mt-0.5' : 'text-blue-600 flex-shrink-0 mt-0.5'} />
                  <div className="flex-1">
                    <p className={`text-sm font-semibold ${viewModel.needsTimeSlotSelection ? 'text-orange-800' : 'text-blue-800'} mb-1`}>
                      {viewModel.durationDifference < 0
                        ? t('bookingManage.durationDecreased', 'Durée réduite')
                        : t('bookingManage.durationIncreased', 'Durée augmentée')}
                    </p>
                    <p className={`text-sm ${viewModel.needsTimeSlotSelection ? 'text-orange-700' : 'text-blue-700'}`}>
                      {viewModel.durationDifference < 0
                        ? t('bookingManage.durationDecreasedMessage', `La durée totale est passée de ${viewModel.originalTotalDuration} min à ${viewModel.totalDuration} min. Veuillez sélectionner un nouveau créneau ci-dessous.`)
                        : t('bookingManage.durationIncreasedMessage', `La durée totale est passée de ${viewModel.originalTotalDuration} min à ${viewModel.totalDuration} min. Veuillez sélectionner un nouveau créneau ci-dessous.`)}
                    </p>
                    {viewModel.selectedSlot && (
                      <p className="text-sm text-green-700 mt-2 font-medium">
                        ✓ {t('bookingManage.newSlotSelected', 'Nouveau créneau sélectionné')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* New time slot selection */}
            {viewModel.loadingSlots ? (
              <div className="flex justify-center py-8">
                <Spinner size="md" />
              </div>
            ) : (
              <div className="space-y-4">
                {/* Available dates */}
                {viewModel.availableDates.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      {t('bookingManage.selectDate', 'Sélectionner une date')}
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {viewModel.availableDates.map((date, index) => {
                        // Créer la date en heure locale plutôt qu'en UTC pour éviter les décalages
                        const [year, month, day] = date.split('-').map(Number);
                        const dateObj = new Date(year, month - 1, day);
                        // Comparer les dates en local sans passer par UTC
                        const selectedDateStr = viewModel.selectedDate ?
                          `${viewModel.selectedDate.getFullYear()}-${String(viewModel.selectedDate.getMonth() + 1).padStart(2, '0')}-${String(viewModel.selectedDate.getDate()).padStart(2, '0')}`
                          : null;
                        const isSelected = selectedDateStr === date;
                        const formattedDate = new Date(date).toLocaleDateString('fr-FR', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        });
                        return (
                          <button
                            key={index}
                            onClick={() => viewModel.loadSlotsForDate(dateObj)}
                            className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                              isSelected
                                ? 'bg-primary text-white border-primary'
                                : 'bg-white text-gray-700 border-gray-200 hover:border-primary hover:bg-primary/5'
                            }`}
                          >
                            {formattedDate}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Available time slots */}
                {viewModel.selectedDate && viewModel.availableSlots.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      {t('bookingManage.selectSlot', 'Sélectionner un créneau')}
                    </p>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-60 overflow-y-auto">
                      {viewModel.availableSlots.map((slot, index) => {
                        const isSelected = viewModel.selectedSlot?.startTime === slot.startTime;
                        return (
                          <button
                            key={index}
                            onClick={() => viewModel.selectSlot(slot)}
                            className={`p-2 rounded-lg border text-sm font-medium transition-all ${
                              isSelected
                                ? 'bg-primary text-white border-primary'
                                : 'bg-white text-gray-700 border-gray-200 hover:border-primary hover:bg-primary/5'
                            }`}
                          >
                            {slot.startTime} - {slot.endTime}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {viewModel.selectedDate && viewModel.availableSlots.length === 0 && !viewModel.loadingSlots && (
                  <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600 text-center">
                    {t('bookingManage.noSlotsAvailable', 'Aucun créneau disponible pour cette date')}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
          {viewModel.isEditMode ? (
            <div className="space-y-3">
              {viewModel.needsTimeSlotSelection && (
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-800 text-center">
                  {t('bookingManage.selectNewSlotRequired', 'Veuillez sélectionner un nouveau créneau pour valider')}
                </div>
              )}
              <Button
                onClick={() => viewModel.saveChanges()}
                disabled={!viewModel.canSaveChanges || viewModel.getUpdating}
                className="w-full py-3 text-base font-semibold bg-primary hover:bg-primary-dark text-white rounded-lg"
              >
                {viewModel.getUpdating
                  ? t('bookingManage.saving', 'Enregistrement...')
                  : t('bookingManage.saveChanges', 'Enregistrer les modifications')}
              </Button>
              <button
                onClick={() => viewModel.cancelChanges()}
                className="w-full py-3 text-base font-medium text-gray-600 hover:text-gray-800"
              >
                {t('bookingManage.cancelChanges', 'Annuler les modifications')}
              </button>
            </div>
          ) : viewModel.isCancelled ? (
            <div className="p-4 bg-red-100 text-center rounded-lg text-red-800 font-medium">
              {t('bookingManage.cancelledDone', 'Cette réservation a été annulée')}
            </div>
          ) : viewModel.isCompleted ? (
            <div className="p-4 bg-green-100 text-center rounded-lg text-green-800 font-medium">
              {t('bookingManage.completedDone', 'Cette réservation est terminée')}
            </div>
          ) : viewModel.isPastOrOngoing ? (
            <div className="p-4 bg-orange-100 text-center rounded-lg text-orange-800 font-medium">
              {t('bookingManage.pastOrOngoing', 'Cette réservation est passée ou en cours et ne peut plus être modifiée')}
            </div>
          ) : (
            <div className="space-y-3">
              <Button
                onClick={() => viewModel.setIsEditMode(true)}
                className="w-full py-3 text-base font-semibold bg-primary hover:bg-primary-dark text-white rounded-lg"
              >
                {t('bookingManage.editBooking', 'Modifier la réservation')}
              </Button>
              <Button
                onClick={() => viewModel.cancelBooking(booking.id)}
                disabled={viewModel.getCancelling || !viewModel.canCancel}
                className="w-full py-3 text-base font-semibold bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {viewModel.getCancelling
                  ? t('bookingManage.canceling', 'Annulation en cours...')
                  : t('bookingManage.cancelBooking', 'Annuler la réservation')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default BookingManage;
